import { Outlet, createFileRoute, useMatch, useNavigate, useParams } from "@tanstack/react-router";

import { SpecHeaderBand } from "@/components/specifications/spec-header-band";
import { usePermissions } from "@/hooks/permissions";
import { useSpecificationSummary } from "@/hooks/specifications";
import { useSpecVersionSwitch } from "@/hooks/spec-version-switch";
import { requireSpecsAccess } from "@/lib/route-guards";
import { specDetailsLanding } from "@/lib/spec-details-landing";
import { parseRouteId } from "@/lib/route-paths";

// The spec layout route — pathless, so it adds no URL segment — whose one job
// is the **Spec header**: the band beneath the app header on the two version
// routes, `$specId/versions/$versionId` and its `/edit` (#680 Q2;
// `client/docs/architecture.md`, "Route-owned chrome"). `AppHeader` learns
// nothing about specs.
//
// Which face the band wears is **which child matched**, never a test of the
// URL string — the donor's `href.includes('edit')` turns a search for "edit"
// into the edit face. The Spec list (`$specType`, #691) and the two legacy
// resolvers are siblings of this route, not children, so no band shows there.
//
// The band reads the same summary query the details loader ensured, keyed by
// the matched child's spec id, so band and frame share one cache entry. A bad
// version id never reaches it: the details loader throws Page not found **at
// this route**, which replaces the band with the 404 beneath the app header
// (#680 Q3b) — spec A's dropdown over a foreign id is the confusion the break
// removes.

export const SPEC_LAYOUT_ROUTE_ID = "/_authed/_chrome/specifications/_spec";

export const Route = createFileRoute("/_authed/_chrome/specifications/_spec")({
  beforeLoad: ({ context }) => {
    requireSpecsAccess(context.currentUser);
  },
  // A not-found renders at the route it is raised at, and its head with it —
  // the child's never runs. The details loader raises it here, so this is
  // where the 404 names itself; on a page that rendered, the child's title is
  // the one that applies.
  head: ({ match }) => (match.status === "notFound" ? { meta: [{ title: "Page not found" }] } : {}),
  component: SpecLayout,
});

function SpecLayout() {
  const params = useParams({ strict: false });
  const { canEditSpecs, canCreateCoreSpecs } = usePermissions();
  const specId = parseRouteId(params.specId ?? "");
  const versionId = parseRouteId(params.versionId ?? "");
  const onEdit =
    useMatch({
      from: "/_authed/_chrome/specifications/_spec/$specId/versions/$versionId_/edit",
      shouldThrow: false,
    }) !== undefined;
  const summary = useSpecificationSummary(specId);
  const { landedVersionPick, selectVersion } = useSpecVersionSwitch(specId);
  const navigate = useNavigate();

  // An id that is not one never gets a band: the child's loader answers it
  // (Page not found at this route, or the base list) while this renders.
  if (specId === null || versionId === null) return <Outlet />;

  return (
    <>
      <SpecHeaderBand
        face={onEdit ? "edit" : "details"}
        specId={specId}
        summary={summary.data ?? undefined}
        versionId={versionId}
        canEditSpecs={canEditSpecs}
        // The core-create roles are the donor's `canEditAnySpec`, Admin and
        // National Editor (`usePermissions.ts:42,63`).
        canEditAnySpec={canCreateCoreSpecs}
        landedVersionPick={landedVersionPick}
        onSelectVersion={selectVersion}
        // The details face's Edit with a draft: a push, as the donor's
        // `navigate` is (`spec-edit-button/actions.ts:24-29`).
        onEditDraft={(draftVersionId) =>
          void navigate({
            to: "/specifications/$specId/versions/$versionId/edit",
            params: { specId: String(specId), versionId: String(draftVersionId) },
          })
        }
        // A publish's landing, or Push Changes' error ending: a push to the
        // version's details (`specDetailsLanding`).
        onLandOnDetails={(landedVersionId) =>
          void navigate(specDetailsLanding(specId, landedVersionId, onEdit))
        }
      />
      <Outlet />
    </>
  );
}
